<template>
    <label class="Checkbox" :class="className">
        <input
            type="checkbox"
            :disabled="disabled"
            :checked="currentValue"
            v-bind="passedAttrs"
            @input="onChange"
        >
        <span class="Checkbox__icon">
            <span class="Checkbox__iconInner">
                <FontAwesomeIcon :icon="faCheck" />
            </span>
        </span>
        <span class="Checkbox__content">
            <slot />
        </span>
    </label>
</template>

<script lang="ts">
    import omit from 'lodash/omit';
    import { Component, Prop, Watch, Vue } from 'vue-property-decorator';

    import { faCheck } from '@fortawesome/free-solid-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    // PRODUCT-26110 Task 12 (COMPONENT_V_MODEL): migrated to the Vue 3 v-model
    // contract. Widely consumed both via v-model and via manual :model-value/
    // @update:model-value bindings across dashboard-commons and tprm-portal.
    @Component({
        emits: [ 'update:modelValue' ],
        inheritAttrs: false,
        components: { FontAwesomeIcon },
    })
    export default class Checkbox extends Vue {
        private faCheck = faCheck;

        @Prop({ type: Boolean, default: false })
        private modelValue!: boolean;

        @Prop({ type: Boolean, default: false })
        private disabled!: boolean;

        private currentValue: boolean = false;

        @Watch('modelValue', { immediate: true })
        private updateCurrentValue() {
            this.currentValue = this.modelValue;
        }

        private get className() {
            return [
                this.currentValue ? 'Checkbox--selected' : '',
                this.disabled ? 'Checkbox--disabled' : '',
                this.$attrs.class,
            ].filter(Boolean);
        }

        // PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): Vue 3 merges listeners into
        // `$attrs` as `onXxx` keys, so `v-bind="$attrs"` alone would now ALSO forward
        // a caller's listener straight onto the native `<input>` -- double-firing it
        // with a raw native Event (native "input" DOM event) in addition to the
        // correctly-typed boolean this component emits itself via
        // `$emit('update:modelValue', ...)` below. Excluding it here (as `$listeners`
        // excluded `'input'` pre-Vue-3) keeps that a single, correctly-typed emission.
        // PRODUCT-26110 Task 12 (COMPONENT_V_MODEL): the caller's listener key is now
        // `onUpdate:modelValue` (v-model's compiled event name), not `onInput`.
        private get passedAttrs(): Record<string, unknown> {
            return omit(this.$attrs, [ 'onUpdate:modelValue' ]);
        }

        private onChange(event: Event) {
            this.currentValue = (event.target as HTMLInputElement).checked;
            this.$emit('update:modelValue', this.currentValue);
        }
    }
</script>
